/**
 * CPU 対局の駆動ロジック — 純粋JS（DOM / three.js 非依存、node でテスト可能）。
 *
 * 「誰が手番を操作するか」の判定と、CPU 着手タイマーの状態機械
 * （思考ディレイ・一時停止・generation counter による古いタイマーの無効化）
 * をここに集約する。実際の setTimeout / AI 呼び出し / applyMove は
 * main.js が注入・実行する。
 *
 * ルール・AI・音のロジックは一切持たない（それぞれ core / ai / audio の
 * 公開I/Fの責務）。
 */

import { AI_LEVELS } from '../ai/index.js';

/** セットアップ画面の操作者選択肢。 */
export const PLAYER_KINDS = ['human', ...AI_LEVELS]; // human | easy | casual | normal | tough | hard

/** UI表示名。 */
export const PLAYER_KIND_LABELS = {
  human: '人間',
  easy: 'CPU（よわい）',
  casual: 'CPU（ややよわい）',
  normal: 'CPU（ふつう）',
  tough: 'CPU（つよめ）',
  hard: 'CPU（つよい）',
};

/** 観戦モードの手間隔（思考ディレイ）。 */
export const SPECTATE_SPEEDS = [
  { id: 'slow', label: '遅い', ms: 1600 },
  { id: 'normal', label: 'ふつう', ms: 800 },
  { id: 'fast', label: '速い', ms: 300 },
];

export const DEFAULT_SPEED_ID = 'normal';

export function speedById(id) {
  return SPECTATE_SPEEDS.find((s) => s.id === id) ?? SPECTATE_SPEEDS[1];
}

/** kind が CPU レベル（easy/casual/normal/tough/hard）か。 */
export function isCpuKind(kind) {
  return AI_LEVELS.includes(kind);
}

/** 両者 CPU = 観戦モード。 */
export function isSpectateMatch(kinds) {
  return kinds.length === 2 && kinds.every(isCpuKind);
}

/**
 * この局面で CPU が自動着手すべきか。
 * state はコア契約の状態（winner / current のみ参照）、kinds は
 * [先手の操作者, 後手の操作者]。
 */
export function cpuShouldAct(state, kinds) {
  return state.winner === null && isCpuKind(kinds[state.current]);
}

/** 人間がこの局面を操作してよいか（盤クリック・能力ボタンの活性判定）。 */
export function humanCanAct(state, kinds) {
  return state.winner === null && !isCpuKind(kinds[state.current]);
}

/**
 * CPU 着手タイマーの状態機械。
 *
 *   const driver = createCpuDriver();
 *   driver.reset();                    // 新しい対局（古いタイマーを全部無効化）
 *   driver.maybeSchedule(should, fn);  // 状態変化後に毎回呼ぶ
 *   driver.setPaused(true/false);      // 観戦の一時停止/再開
 *   driver.setDelay(ms);               // 観戦の速度切替
 *
 * 安全性:
 *   - reset() は generation counter を進める。発火済みタイマーのコールバックは
 *     自分の generation と現在値を比較し、古ければ何もしない（clearTimeout が
 *     何らかの理由で効かなくても手が混入しない二重防御）。
 *   - maybeSchedule() は常に既存タイマーをキャンセルしてから張り直す
 *     （同時に2つのタイマーが生きることはない）。
 *   - 一時停止中はタイマーを張らず、保留中のタイマーもキャンセルする。
 *     発火の瞬間に paused だった場合も何もしない。
 *
 * schedule / cancel はテスト用に注入可能（既定は setTimeout / clearTimeout）。
 */
export function createCpuDriver({
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (handle) => clearTimeout(handle),
} = {}) {
  let generation = 0;
  let handle = null;
  let paused = false;
  let delayMs = speedById(DEFAULT_SPEED_ID).ms;

  function cancelTimer() {
    if (handle !== null) {
      cancel(handle);
      handle = null;
    }
  }

  return {
    get generation() {
      return generation;
    },
    get paused() {
      return paused;
    },
    get delayMs() {
      return delayMs;
    },
    /** 保留中のタイマーがあるか（HUD の「CPU思考中…」表示用）。 */
    get pending() {
      return handle !== null;
    },

    /** 観戦速度の変更。次の maybeSchedule から適用。 */
    setDelay(ms) {
      delayMs = ms;
    },

    /**
     * 新しい対局 / セットアップ画面へ戻る際に呼ぶ。
     * 進行中のタイマーをキャンセルし、generation を進めて
     * 「以後、古い対局のコールバックは決して発火しない」ことを保証する。
     * 一時停止状態も解除する。
     */
    reset() {
      generation += 1;
      paused = false;
      cancelTimer();
      return generation;
    },

    /** 一時停止/再開。停止時は保留中のタイマーもキャンセル。 */
    setPaused(next) {
      paused = !!next;
      if (paused) cancelTimer();
      return paused;
    },

    /**
     * shouldAct が真かつ非停止なら、思考ディレイ後に fire() を予約する。
     * 予約できたら true。既存の保留タイマーは常に置き換える。
     */
    maybeSchedule(shouldAct, fire) {
      cancelTimer();
      if (!shouldAct || paused) return false;
      const gen = generation;
      handle = schedule(() => {
        handle = null;
        if (gen !== generation || paused) return; // 古い対局 or 停止中: 無視
        fire();
      }, delayMs);
      return true;
    },
  };
}
